/* ============================================================================
   PANEL OEI — BK117 B-2

   El módulo `domain/oei.js` estaba escrito, probado y **sin que nadie lo
   importara**: la evaluación con un motor inoperativo no se podía hacer desde
   la aplicación.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ ESTE PANEL PIDE DATOS EN VEZ DE CALCULARLOS

   Las curvas de peso máximo OEI del BK117 B-2 están publicadas como
   **nomograma** —Figura 5-2 del RFM—. Un nomograma escaneado no se digitaliza
   punto por punto con fiabilidad, y un error de lectura en esa curva es un
   error de cientos de kilos.

   Así que el panel **no inventa la curva**: pide el valor que el piloto lee
   del manual para su altitud de densidad y temperatura, y a partir de ahí
   aplica las reglas que el RFM sí publica como tabla — reserva de ascenso,
   crédito por viento, envolvente de azimut.

   Mientras no haya un valor leído del manual, responde «sin carta OEI» en vez
   de un número. Un número inventado acá mata gente.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ ES ESPECÍFICO DE UN BIMOTOR

   En un monomotor la pregunta es «¿puedo sostener el estacionario?». En un
   bimotor hay una segunda que decide la operación: **«si se me para un motor
   AHORA, ¿el que queda alcanza?»**.

   Con los dos motores el BK117 llega a 3350 kg; con uno solo, al nivel del mar
   y 15 °C, el máximo para sostener estacionario fuera del efecto suelo baja a
   2770 kg. Son 580 kg —siete personas— que separan «puedo despegar» de «puedo
   despegar y sobrevivir a una falla».
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import { correspondeOei } from "../../domain/oei.js";
import React, { useMemo, useState } from "react";

import {
  evaluarOEI, vientoDentroDeEnvolvente,
  REGIMENES_POTENCIA_BK117, RESERVA_ASCENSO_OEI, VY_OEI_KIAS,
  ENVOLVENTE_VIENTO_BK117,
} from "../../domain/oei.js";
import {
  comprobarEnvolvente, PROCEDENCIA_ENVOLVENTE, LIMITES,
  BOLETIN_ALTURA,
} from "../../domain/envolventeBK117.js";
import "./oei.css";

/** La aeronave a la que aplica. */
export const AERONAVE = {
  tipo: "BK117 B-2",
  matricula: "OB-2240P",
  rfm: "RFM BK117 B-2, sección 5.8A",
  nota: "Datos y reglas transcritos del manual de vuelo de esta aeronave. "
    + "No aplican a otro tipo ni a otra variante.",
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function PanelOEI({
  pesoBrutoKg = null,
  altitudPresionFt = null,
  altitudDensidadFt = null,
  vientoKt = null,
  oatC = null,
  azimutRelativoGrados = null,
  matricula = "",
  serie = null,
  /* Cuántos motores tiene la aeronave CARGADA. Sin este dato el panel no
     puede saber si corresponde, y se abstiene. */
  motores = null,
}) {
  /* El valor leído del nomograma. Se guarda por aeronave: es una lectura del
     manual, no un dato del vuelo. */
  const [pesoCartaKg, setPesoCartaKg] = useState(() => {
    try { return localStorage.getItem("hcOeiPesoCarta") || ""; } catch { return ""; }
  });
  const [pesoCartaVientoKg, setPesoCartaVientoKg] = useState(() => {
    try { return localStorage.getItem("hcOeiPesoCartaViento") || ""; } catch { return ""; }
  });
  const [reservaFtMin, setReservaFtMin] = useState(0);

  /* El equipo de tiempo frío cambia el mínimo de −35 a −45 °C. Es una
     configuración de la aeronave, no del vuelo: se recuerda. */
  /* El boletín de altura es una modificación de la aeronave: cambia el techo
     de 15000 a 18000 ft. Se recuerda, como el equipo de tiempo frío. */
  const [boletin, setBoletin] = useState(() => {
    try { return localStorage.getItem("hcOeiBoletin") === "si"; } catch { return false; }
  });

  const cambiarBoletin = (valor) => {
    setBoletin(valor);
    try { localStorage.setItem("hcOeiBoletin", valor ? "si" : "no"); } catch { /* modo privado */ }
  };

  const [kitFrio, setKitFrio] = useState(() => {
    try { return localStorage.getItem("hcOeiKitFrio") === "si"; } catch { return false; }
  });

  const cambiarKit = (valor) => {
    setKitFrio(valor);
    try { localStorage.setItem("hcOeiKitFrio", valor ? "si" : "no"); } catch { /* modo privado */ }
  };

  /* La envolvente sale de la CARTA del operador. La altitud de densidad de la
     carta manda sobre la calculada: no es lo mismo un número del documento
     aprobado que uno de la fórmula general. */
  const envolvente = useMemo(() => comprobarEnvolvente({
    altitudPresionFt: num(altitudPresionFt),
    oatC: num(oatC),
    altitudDensidadFt: num(altitudDensidadFt),
    kitFrioInstalado: kitFrio,
    pesoBrutoKg: num(pesoBrutoKg),
    serie,
    boletinIncorporado: boletin,
    /* La fase que importa acá es el despegue: el límite de 17000 ft es de
       estacionario en efecto suelo, despegue y aterrizaje, y este panel se
       mira justamente antes de despegar. */
    fase: "despegue",
  }), [altitudPresionFt, oatC, altitudDensidadFt, kitFrio, pesoBrutoKg, serie, boletin]);

  const daEfectiva = envolvente.altitudDensidadFt;

  const guardarCarta = (valor, clave, setter) => {
    setter(valor);
    try { localStorage.setItem(clave, valor); } catch { /* modo privado */ }
  };

  const resultado = useMemo(() => evaluarOEI({
    pesoBrutoKg: num(pesoBrutoKg),
    pesoMaximoOeiSinVientoKg: num(pesoCartaKg),
    pesoMaximoOeiConVientoKg: num(pesoCartaVientoKg),
    reservaAscensoFtMin: Number(reservaFtMin) || 0,
  }), [pesoBrutoKg, pesoCartaKg, pesoCartaVientoKg, reservaFtMin]);

  const viento = useMemo(() => vientoDentroDeEnvolvente(
    num(azimutRelativoGrados), num(vientoKt), num(altitudDensidadFt),
  ), [azimutRelativoGrados, vientoKt, altitudDensidadFt]);

  /* La aeronave cargada puede no ser esta. Aplicar reglas del BK117 a otro
     tipo sería el peor error posible de este panel. */
  const otraAeronave = matricula && matricula.toUpperCase() !== AERONAVE.matricula;

  /* ═══ ¿CORRESPONDE EL ANÁLISIS? ═══

     Va acá y no arriba: **React exige que todos los hooks se llamen en el
     mismo orden en cada dibujado**. Una salida temprana antes de los `useState`
     los saltea cuando la aeronave es monomotor, y al cambiar de aeronave React
     encuentra un número distinto de hooks y rompe.

     Lo intenté arriba y el lint lo agarró con seis errores. Es la clase de
     error que en producción se manifiesta como una pantalla en blanco al
     cambiar de aeronave, sin decir por qué.

     El cálculo de arriba se hace igual y se descarta: cuesta nada, y es el
     precio de respetar la regla.

     ─────────────────────────────────────────────────────────────────────────
     El panel se estaba dibujando con el AS350 cargado y mostraba «máximo 0 kg,
     excedido 2009,8 kg» — números del BK117 sobre una aeronave sin segundo
     motor. Un cero ahí no dice «no aplica»: dice «no puedes despegar». */
  const aplica = correspondeOei(motores);

  if (aplica.corresponde === false) return null;

  if (aplica.corresponde === null) {
    /* No saber cuántos motores tiene NO es lo mismo que saber que tiene uno.
       Esconderlo afirmaría que no aplica sobre un bimotor cuyo perfil quedó
       incompleto, y ahí sí haría falta. */
    return (
      <section className="oei">
        <header className="oei__cabecera">
          <h3 className="oei__titulo">Un motor inoperativo (OEI)</h3>
        </header>
        <p className="oei__aviso" role="alert">{aplica.motivo}</p>
      </section>
    );
  }

  return (
    <section className="oei">
      <header className="oei__cabecera">
        <h3 className="oei__titulo">Un motor inoperativo (OEI)</h3>
        <span className="oei__aeronave">{AERONAVE.tipo} · {AERONAVE.matricula}</span>
      </header>

      {otraAeronave && (
        <p className="oei__alerta" role="alert">
          La aeronave cargada es <strong>{matricula}</strong>. Este análisis usa
          datos del <strong>{AERONAVE.tipo} {AERONAVE.matricula}</strong> y no
          aplica a otro tipo ni a otra variante.
        </p>
      )}

      <p className="oei__intro">
        Con los dos motores el BK117 llega a 3350 kg. Con uno solo, al nivel del
        mar y 15 °C, el máximo para sostener el estacionario fuera del efecto
        suelo baja a <strong>2770 kg</strong>. Esa diferencia separa «puedo
        despegar» de «puedo despegar y sobrevivir a una falla».
      </p>

      {/* La envolvente primero: si el vuelo está fuera de límites, el peso
          máximo OEI es una pregunta posterior. */}
      <div className="oei__envolvente">
        <div className="oei__cifra">
          <span>Altitud de densidad</span>
          <strong>
            {daEfectiva !== null ? `${Math.round(daEfectiva)} ft` : "—"}
          </strong>
          <small>
            {envolvente.origenDA === "carta"
              ? "De la carta del operador"
              : envolvente.origenDA === "calculada"
                ? "Calculada, no leída de la carta"
                : "Sin datos suficientes"}
          </small>
        </div>

        <div className="oei__cifra">
          <span>Máximo de operación</span>
          <strong>{envolvente.maximoOperacionFt} ft</strong>
          <small>{boletin ? `Con ${BOLETIN_ALTURA}` : `S/N ${serie || "sin declarar"}`}</small>
        </div>

        <label className="oei__campo--linea">
          <input
            type="checkbox"
            checked={boletin}
            onChange={(e) => cambiarBoletin(e.target.checked)}
          />
          <span>{BOLETIN_ALTURA} incorporado</span>
        </label>

        <label className="oei__campo--linea">
          <input
            type="checkbox"
            checked={kitFrio}
            onChange={(e) => cambiarKit(e.target.checked)}
          />
          <span>
            Equipo de tiempo frío instalado
            {" "}(mínimo {kitFrio ? LIMITES.temperaturaMinimaConKitC.valor
              : LIMITES.temperaturaMinimaSinKitC.valor} °C)
          </span>
        </label>
      </div>

      {envolvente.hallazgos.length > 0 && (
        <ul className="oei__hallazgos">
          {envolvente.hallazgos.map((h) => (
            <li key={h.texto} className={`oei__hallazgo oei__hallazgo--${h.nivel}`}>{h.texto}</li>
          ))}
        </ul>
      )}

      <div className="oei__campos">
        <label className="oei__campo">
          <span>Peso máximo OEI de la carta (kg)</span>
          <input
            type="number"
            inputMode="decimal"
            value={pesoCartaKg}
            onChange={(e) => guardarCarta(e.target.value, "hcOeiPesoCarta", setPesoCartaKg)}
            placeholder="Leído de la Figura 5-2"
          />
          <small>
            Para la altitud de densidad y temperatura de hoy
            {daEfectiva !== null && ` (${Math.round(daEfectiva)} ft)`}.
          </small>
        </label>

        <label className="oei__campo">
          <span>Con crédito por viento (kg)</span>
          <input
            type="number"
            inputMode="decimal"
            value={pesoCartaVientoKg}
            onChange={(e) => guardarCarta(e.target.value, "hcOeiPesoCartaViento", setPesoCartaVientoKg)}
            placeholder="Opcional"
          />
          <small>Solo si el viento está dentro de la envolvente.</small>
        </label>

        <label className="oei__campo">
          <span>Reserva de ascenso</span>
          <select value={reservaFtMin} onChange={(e) => setReservaFtMin(e.target.value)}>
            <option value={0}>Sin reserva (solo estacionario)</option>
            {RESERVA_ASCENSO_OEI.map((r) => (
              <option key={r.regimenFtMin} value={r.regimenFtMin}>
                {r.regimenFtMin} ft/min (−{r.restaKg} kg)
              </option>
            ))}
          </select>
          <small>RFM 5.8A.3</small>
        </label>
      </div>

      {/* El resultado. Sin carta cargada NO se muestra un número. */}
      <div className={`oei__resultado${resultado.cumple === false ? " es-alto" : ""}`}>
        {resultado.valido ? (
          <>
            <div className="oei__cifra">
              <span>Máximo OEI</span>
              <strong>{resultado.pesoMaximoKg} kg</strong>
            </div>
            {resultado.margenKg !== null && (
              <div className="oei__cifra">
                <span>{resultado.margenKg >= 0 ? "Margen" : "Excedido"}</span>
                <strong>{Math.abs(resultado.margenKg)} kg</strong>
              </div>
            )}
          </>
        ) : (
          <p className="oei__sin-carta">
            Carga el peso máximo OEI leído de la carta del manual para esta
            altitud y temperatura. Sin ese dato no se puede afirmar que el
            despegue sea sostenible con un motor inoperativo.
          </p>
        )}
      </div>

      {resultado.hallazgos.length > 0 && (
        <ul className="oei__hallazgos">
          {resultado.hallazgos.map((h) => (
            <li key={h.texto} className={`oei__hallazgo oei__hallazgo--${h.nivel}`}>{h.texto}</li>
          ))}
        </ul>
      )}

      {/* El viento solo da crédito dentro de la envolvente. Fuera de ella, el
          crédito no aplica y usarlo sería un peso máximo inflado. */}
      {viento?.motivo && (
        <p className={`oei__hallazgo oei__hallazgo--${viento.dentro ? "info" : "precaucion"}`}>
          {viento.motivo}
        </p>
      )}

      <details className="oei__referencia">
        <summary>Regímenes de potencia y velocidad</summary>
        <table className="oei__tabla">
          <thead>
            <tr><th>Régimen</th><th>TOT °C</th><th>Torque %</th><th>Nota</th></tr>
          </thead>
          <tbody>
            {Object.entries(REGIMENES_POTENCIA_BK117).map(([clave, r]) => (
              <tr key={clave}>
                <td>{r.etiqueta}</td>
                <td>{r.totC ?? "—"}</td>
                <td>{r.torquePct ?? "—"}</td>
                <td>{r.nota || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="oei__nota">
          Velocidad de mejor ascenso con un motor: <strong>{VY_OEI_KIAS} KIAS</strong>.
          Envolvente de viento: {ENVOLVENTE_VIENTO_BK117.omnidireccionalKt} kt
          omnidireccional, {ENVOLVENTE_VIENTO_BK117.sectorProaKt} kt dentro de
          ±{ENVOLVENTE_VIENTO_BK117.sectorProaGrados}° de proa, demostrada hasta
          {" "}{ENVOLVENTE_VIENTO_BK117.altitudMaximaDensidadFt} ft de altitud de densidad.
        </p>
      </details>

      <p className="oei__procedencia">
        {AERONAVE.rfm} · {AERONAVE.nota}
      </p>
      <p className="oei__procedencia">
        Envolvente y altitud de densidad: {PROCEDENCIA_ENVOLVENTE.fuente}.
        {" "}{PROCEDENCIA_ENVOLVENTE.verificacion}
      </p>
      {/* Que la carta de densidad esté cargada NO significa que la de OEI lo
          esté. Confundirlas haría creer que el peso máximo se calcula solo. */}
      <p className="oei__pendiente">{PROCEDENCIA_ENVOLVENTE.advertencia}</p>
    </section>
  );
}
